import React, { useState, useMemo } from "react";
import "../UIcss/Modals.css";
import Pagination from "./Pagination.jsx";
import CodeViewer from "./CodeViewer.jsx";
import { FaWindowRestore } from "react-icons/fa";
import { LuCopy } from "react-icons/lu";

// --- RENDERER COMPONENT ---

const ModalRenderer = ({ item, index, onShowCode, onOpenDemo }) => {
  return (
    <div className="modal-card" onClick={() => onOpenDemo(item)}>
      <div className="modal-meta-header">
        <span className="modal-label">
          {index + 1}. {item.name}
        </span>
        <button
          className="modal-copy-btn"
          onClick={(e) => {
            e.stopPropagation();
            onShowCode(item);
          }}
          title="View Code"
        >
          <LuCopy size={14} />
        </button>
      </div>

      <div className="modal-preview-area">
        <div className="modal-preview-scaled">
          <div className={`modal-base ${item.themeClass}`}>
            <div className="modal-header-inner">
              <span>{item.name}</span>
              <span>×</span>
            </div>
            <div className="modal-body-inner">{item.desc}</div>
            <div className="modal-footer-inner">
              <button className="modal-btn btn-secondary">Close</button>
              <button className="modal-btn btn-primary">Action</button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer-info">
        <div className="modal-animation-name">{item.animation}</div>
      </div>
    </div>
  );
};

const Modals = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;

  const [viewerState, setViewerState] = useState({
    isOpen: false,
    name: "",
    jsx: "",
    css: "",
  });

  const [activeDemo, setActiveDemo] = useState(null);

  const modalStyles = [
    {
      name: "Cyber Synthwave",
      theme: "theme-cyber",
      desc: "Neon pink & blue gradient glow.",
    },
    {
      name: "Electric Lime",
      theme: "theme-electric",
      desc: "High-contrast neon green.",
    },
    {
      name: "Sunset Blaze",
      theme: "theme-sunset",
      desc: "Fiery orange to pink gradient.",
    },
    {
      name: "Golden Luxury",
      theme: "theme-gold",
      desc: "Rich gold on dark matter.",
    },
    {
      name: "Midnight Purple",
      theme: "theme-gold",
      desc: "Deep royal purple glow.",
    },
    {
      name: "Arctic Frost",
      theme: "theme-gold",
      desc: "Icy blue-white cool gradient.",
    },
    {
      name: "Vampire Blood",
      theme: "theme-gold",
      desc: "Deep crimson red gradient.",
    },
    {
      name: "Terminal Green",
      theme: "theme-terminal",
      desc: "Matrix code rain green.",
    },
    { name: "Bumblebee", theme: "theme-gold", desc: "Stark yellow and black." },
    {
      name: "Holographic",
      theme: "theme-gold",
      desc: "Prismatic RGB shift glass.",
    },
  ];

  const animations = [
    "Fade In",
    "Slide Up",
    "Zoom In",
    "Rotate In",
    "Slide Down",
    "Slide Left",
    "Slide Right",
    "Flip Vertical",
    "Bounce In",
    "Pulse",
  ];

  const getAnimationStyles = (anim) => {
    const name = anim.replace(" ", "-").toLowerCase();
    switch (anim) {
      case "Fade In":
        return `@keyframes ${name} { from { opacity: 0; } to { opacity: 1; } }`;
      case "Slide Up":
        return `@keyframes ${name} { from { opacity: 0; transform: translateY(50px); } to { opacity: 1; transform: translateY(0); } }`;
      case "Zoom In":
        return `@keyframes ${name} { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }`;
      case "Rotate In":
        return `@keyframes ${name} { from { opacity: 0; transform: rotate(-15deg) scale(0.8); } to { opacity: 1; transform: rotate(0) scale(1); } }`;
      case "Slide Down":
        return `@keyframes ${name} { from { opacity: 0; transform: translateY(-50px); } to { opacity: 1; transform: translateY(0); } }`;
      case "Slide Left":
        return `@keyframes ${name} { from { opacity: 0; transform: translateX(50px); } to { opacity: 1; transform: translateX(0); } }`;
      case "Slide Right":
        return `@keyframes ${name} { from { opacity: 0; transform: translateX(-50px); } to { opacity: 1; transform: translateX(0); } }`;
      case "Flip Vertical":
        return `@keyframes ${name} { from { opacity: 0; transform: perspective(400px) rotateX(90deg); } to { opacity: 1; transform: perspective(400px) rotateX(0); } }`;
      case "Bounce In":
        return `@keyframes ${name} { 0% { opacity: 0; transform: scale(0.3); } 50% { opacity: 1; transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { transform: scale(1); } }`;
      case "Pulse":
        return `@keyframes ${name} { 0% { transform: scale(1); } 50% { transform: scale(1.02); } 100% { transform: scale(1); } }`;
      default:
        return "";
    }
  };

  const getThemeStyles = (theme) => {
    switch (theme) {
      case "theme-cyber":
        return `.modal.theme-cyber { border-color: #ff00ff; box-shadow: 0 0 30px rgba(255, 0, 255, 0.3); }`;
      case "theme-electric":
        return `.modal.theme-electric { border-color: #00ff00; box-shadow: 0 0 30px rgba(0, 255, 0, 0.3); }`;
      case "theme-sunset":
        return `.modal.theme-sunset { border-color: #ff4500; box-shadow: 0 0 30px rgba(255, 69, 0, 0.3); }`;
      case "theme-gold":
        return `.modal.theme-gold { border-color: #ffd700; box-shadow: 0 0 30px rgba(255, 215, 0, 0.3); }`;
      case "theme-terminal":
        return `.modal.theme-terminal { border: 2px solid #0f0; color: #0f0; box-shadow: inset 0 0 20px rgba(0, 255, 0, 0.2); } .modal.theme-terminal span, .modal.theme-terminal p { color: #0f0 !important; }`;
      default:
        return "";
    }
  };

  const allItems = useMemo(() => {
    let items = [];
    let idCounter = 1;
    modalStyles.forEach((style) => {
      animations.forEach((anim) => {
        const animClass = `animation-${anim.replace(" ", "-").toLowerCase()}`;
        const animCSS = getAnimationStyles(anim);
        const themeCSS = getThemeStyles(style.theme);

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${style.name} Modal - CodePulse UI</title>
    <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&family=Inter:wght@400;500&display=swap" rel="stylesheet">
    <style>
        /* Overlay Styles */
        .modal-overlay { 
          position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
          background: rgba(0,0,0,0.8); display: flex; align-items: center; 
          justify-content: center; z-index: 1000; 
        }

        /* Modal Base Styles */
        .modal { 
          width: 450px; background: #1a1a1a; border-radius: 12px; 
          overflow: hidden; border: 1px solid rgba(255,255,255,0.1); 
          box-shadow: 0 20px 40px rgba(0,0,0,0.5); position: relative; 
          font-family: 'Inter', sans-serif; color: white;
        }

        .modal-header { 
          padding: 15px 20px; background: rgba(255,255,255,0.03); 
          display: flex; justify-content: space-between; align-items: center; 
          border-bottom: 1px solid rgba(255,255,255,0.05); 
        }

        .modal-header span { 
          font-family: 'Orbitron', sans-serif; font-size: 14px; 
          color: #ffd700; letter-spacing: 1px; 
        }

        .close-x { 
          cursor: pointer; font-size: 20px; color: #ffd700; 
        }

        .modal-body { padding: 30px 20px; color: #ccc; line-height: 1.6; }

        .modal-footer { 
          padding: 15px 20px; background: rgba(0,0,0,0.2); 
          display: flex; justify-content: flex-end; gap: 12px; 
          border-top: 1px solid rgba(255,255,255,0.03); 
        }

        /* Button Styles */
        .button { 
          padding: 10px 20px; border-radius: 6px; border: none; 
          cursor: pointer; font-family: 'Orbitron', sans-serif; 
          font-size: 11px; transition: all 0.3s ease; 
        }

        .primary { background: #ffd700; color: #000; font-weight: bold; }
        .secondary { background: rgba(255,255,255,0.05); color: #888; border: 1px solid rgba(255,255,255,0.1); }

        /* Theme: ${style.name} */
        ${themeCSS}

        /* Animation: ${anim} */
        .${animClass} { 
          animation: ${anim.replace(" ", "-").toLowerCase()} 0.5s ease forwards; 
        }
        ${animCSS}
    </style>
</head>
<body>
    <div class="modal-overlay">
        <div class="modal ${style.theme} ${animClass}">
            <div class="modal-header">
                <span>${style.name}</span>
                <span class="close-x" onclick="document.querySelector('.modal-overlay').style.display='none'">×</span>
            </div>
            <div class="modal-body">
                <p>This is a <strong>${style.name}</strong> modal with <strong>${anim}</strong> animation.</p>
                <p>${style.desc}</p>
            </div>
            <div class="modal-footer">
                <button class="secondary" onclick="document.querySelector('.modal-overlay').style.display='none'">Cancel</button>
                <button class="primary">Confirm Action</button>
            </div>
        </div>
    </div>
</body>
</html>`;

        const fullCSS = `/* Overlay Styles */
.modal-overlay { 
  position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
  background: rgba(0,0,0,0.8); display: flex; align-items: center; 
  justify-content: center; z-index: 1000; 
}

/* Modal Base Styles */
.modal { 
  width: 450px; background: #1a1a1a; border-radius: 12px; 
  overflow: hidden; border: 1px solid rgba(255,255,255,0.1); 
  box-shadow: 0 20px 40px rgba(0,0,0,0.5); position: relative; 
  font-family: 'Inter', sans-serif;
}

.modal-header { 
  padding: 15px 20px; background: rgba(255,255,255,0.03); 
  display: flex; justify-content: space-between; align-items: center; 
  border-bottom: 1px solid rgba(255,255,255,0.05); 
}

.modal-header span { 
  font-family: 'Orbitron', sans-serif; font-size: 14px; 
  color: #ffd700; letter-spacing: 1px; 
}

.close-x { 
  cursor: pointer; font-size: 20px; color: #ffd700; 
}

.modal-body { padding: 30px 20px; color: #ccc; line-height: 1.6; }

.modal-footer { 
  padding: 15px 20px; background: rgba(0,0,0,0.2); 
  display: flex; justify-content: flex-end; gap: 12px; 
  border-top: 1px solid rgba(255,255,255,0.03); 
}

/* Button Styles */
button { 
  padding: 10px 20px; border-radius: 6px; border: none; 
  cursor: pointer; font-family: 'Orbitron', sans-serif; 
  font-size: 11px; transition: all 0.3s ease; 
}

.primary { background: #ffd700; color: #000; font-weight: bold; }
.secondary { background: rgba(255,255,255,0.05); color: #888; border: 1px solid rgba(255,255,255,0.1); }

/* Theme: ${style.name} */
${themeCSS}

/* Animation: ${anim} */
.${animClass} { 
  animation: ${anim.replace(" ", "-").toLowerCase()} 0.5s ease forwards; 
}
${animCSS}`;

        items.push({
          id: idCounter++,
          name: style.name,
          desc: style.desc,
          animation: anim,
          themeClass: style.theme,
          jsx: html,
          css: fullCSS,
        });
      });
    });
    return items;
  }, []);

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const currentItems = useMemo(() => {
    return allItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);
  }, [indexOfFirstItem, allItems]);

  const totalPages = Math.ceil(allItems.length / itemsPerPage);

  const handleShowCode = (item) => {
    setViewerState({
      isOpen: true,
      name: `${item.name} (${item.animation})`,
      jsx: item.jsx,
      css: item.css,
    });
  };

  const handleOpenDemo = (item) => setActiveDemo(item);

  return (
    <div className="modals-page-wrapper">
      <div className="library-header">
        <div className="header-content">
          <h1>
            <FaWindowRestore /> Modals <span>Library</span>
          </h1>
        </div>
      </div>

      <div className="modal-grid">
        {currentItems.map((item, index) => (
          <ModalRenderer
            key={item.id}
            item={item}
            index={indexOfFirstItem + index}
            onShowCode={handleShowCode}
            onOpenDemo={handleOpenDemo}
          />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={allItems.length}
        itemsPerPage={itemsPerPage}
      />

      <CodeViewer
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        componentName={viewerState.name}
        jsxCode={viewerState.jsx}
        cssCode={viewerState.css}
      />

      {/* Full Screen Live Demo Overlay */}
      {activeDemo && (
        <div
          className="modal-overlay-live"
          onClick={() => setActiveDemo(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(12px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className={`modal-base ${activeDemo.themeClass}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "500px",
              maxWidth: "90%",
              boxShadow: "0 30px 60px rgba(0,0,0,0.8)",
            }}
          >
            <div
              className="modal-header-inner"
              style={{ padding: "15px 20px" }}
            >
              <span style={{ fontFamily: "Orbitron", color: "#ffd700" }}>
                {activeDemo.name} Demo
              </span>
              <span
                onClick={() => setActiveDemo(null)}
                style={{ cursor: "pointer", fontSize: "20px" }}
              >
                ×
              </span>
            </div>
            <div
              className="modal-body-inner"
              style={{ padding: "40px 25px", textAlign: "center" }}
            >
              <h2
                style={{
                  color: "#fff",
                  marginBottom: "15px",
                  fontFamily: "Orbitron",
                }}
              >
                {activeDemo.name}
              </h2>
              <p>{activeDemo.desc}</p>
              <p style={{ opacity: 0.6, fontSize: "12px", marginTop: "20px" }}>
                Animation: {activeDemo.animation}
              </p>
            </div>
            <div className="modal-footer-inner" style={{ padding: "20px" }}>
              <button
                onClick={() => setActiveDemo(null)}
                className="modal-btn btn-secondary"
                style={{ padding: "10px 20px" }}
              >
                Close
              </button>
              <button
                onClick={() => handleShowCode(activeDemo)}
                className="modal-btn btn-primary"
                style={{ padding: "10px 25px" }}
              >
                Get Code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Modals;
